import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../../../services/auth/authService';

/**
 * Handles the OAuth 2.0 Authorization Code + PKCE callback from PingFederate.
 *
 * Responsibilities:
 *  1. Validate the `state` parameter to prevent CSRF attacks
 *  2. Exchange the authorization `code` for tokens via authService
 *  3. Persist all tokens to sessionStorage (must match authService's storage)
 *  4. Clean up PKCE artifacts from sessionStorage
 *  5. Redirect to the post-login destination or /home
 *
 * Uses a ref guard to prevent double-invocation in React StrictMode,
 * which would send the one-time authorization code twice and fail.
 */
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();

  /**
   * Prevents the callback handler from running twice in React StrictMode.
   * The authorization code is single-use — a second exchange attempt fails.
   */
  const hasHandled = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-invocation
    if (hasHandled.current) return;
    hasHandled.current = true;

    const handleCallback = async (): Promise<void> => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');

        if (error) {
          throw new Error(`OAuth error returned by PingFed: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code in callback URL');
        }

        const storedState = sessionStorage.getItem('oauth_state');
        const storedVerifier = sessionStorage.getItem('oauth_code_verifier');

        // Guard: PKCE artifacts are missing — this tab has no active login flow.
        // Happens when a user opens a bookmarked /auth/callback URL in a fresh tab
        // (sessionStorage is empty). Restart login cleanly instead of throwing.
        if (!storedState || !storedVerifier) {
          console.warn(
            'AuthCallback: PKCE state/verifier missing — likely a stale bookmark or fresh-tab open. Restarting login.'
          );
          sessionStorage.removeItem('oauth_state');
          sessionStorage.removeItem('oauth_code_verifier');
          navigate('/login', { replace: true });
          return;
        }

        // Guard: state parameter does not match what was stored when login began.
        // A genuine CSRF attempt would trigger this, but so would a race where
        // initiateLogin() fired twice and overwrote the stored state. Either way
        // the safe recovery is to restart the login flow, not to crash.
        if (state !== storedState) {
          console.warn(
            'AuthCallback: State mismatch — possible CSRF or overwritten PKCE state. Restarting login.'
          );
          sessionStorage.removeItem('oauth_state');
          sessionStorage.removeItem('oauth_code_verifier');
          navigate('/login', { replace: true });
          return;
        }

        const { accessToken, refreshToken, idToken, userId } =
          await authService.exchangeCodeForToken(code);

        // CRITICAL: store tokens in sessionStorage — authService, checkAndRefreshAuth,
        // and ProtectedRoute ALL read from sessionStorage. Using localStorage here
        // breaks every downstream token read and causes an infinite auth loop.
        sessionStorage.setItem('accessToken', accessToken);
        sessionStorage.setItem('refreshToken', refreshToken);
        sessionStorage.setItem('idToken', idToken);

        // Only persist userId if it was resolved — avoid storing the string "undefined"
        if (userId) {
          sessionStorage.setItem('userId', userId);
        }

        // Remove PKCE artifacts — code verifier and state are single-use
        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_code_verifier');

        // Clean the OAuth params out of the browser URL without adding a history entry
        window.history.replaceState({}, document.title, '/');

        // Redirect to the originally requested page if saved, otherwise default to /home
        const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
        sessionStorage.removeItem('postLoginRedirect');

        navigate(postLoginRedirect && postLoginRedirect.startsWith('/') ? postLoginRedirect : '/home', {
          replace: true,
        });
      } catch (error) {
        console.error('OAuth callback failed:', error);
        // Clear any partial state before redirecting to avoid stale artefacts
        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_code_verifier');
        navigate('/error=auth_failed', { replace: true });
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <h2>Processing authentication…</h2>
      <p>Redirecting you to the dashboard…</p>
    </div>
  );
};

export default AuthCallback;